import type { Vec2 } from './vec';
import type { Triangle } from './triangle';
import { shoelaceArea, regularPolygonVertices } from './polygon';

/**
 * Describes the pose of a piece: rotation about a point, then translation.
 * Semantics: transform p as: rotateAround(p, about, rotateDeg) then add translate.
 */
export interface PiecePose {
  translate: Vec2;
  rotateDeg: number;
  about: Vec2;
}

/**
 * A single dissection piece: polygon + start/end poses.
 */
export interface Piece {
  id: string;
  polygon: readonly Vec2[];
  start: PiecePose;
  end: PiecePose;
}

/**
 * Complete dissection plan: pieces + source/target outlines.
 */
export interface PiecePlan {
  pieces: readonly Piece[];
  sourceOutline: readonly Vec2[];
  targetOutline: readonly Vec2[];
}

/**
 * Perigal's pinwheel dissection: right triangle with legs a (smaller) and b (larger).
 * Hypotenuse c = √(a²+b²).
 *
 * Source: the a×a square plus the b×b square (placed right of a-square).
 * The b-square is cut by two lines through its centre, one parallel and one
 * perpendicular to the hypotenuse (directions h = (b/c, a/c) and h⊥ = (−a/c, b/c)).
 * This yields four quadrilateral pieces Q1–Q4.
 *
 * Target outline: the c×c hypotenuse square.
 *
 * End-pose arrangement (DONE_WITH_CONCERNS — see note):
 *   The a-square is placed at the centre of the c-square, rotated by θ = atan2(a,b),
 *   so its sides align with h and h⊥.  The four Q-pieces are translated to positions
 *   inside the c-square that avoid all strict interior overlaps.  The arrangement is not
 *   a perfect rigid-body tiling: the wing regions left by the rotated a-square are
 *   six-vertex hexagons whereas each Q-piece is a four-vertex quadrilateral, so no rigid
 *   motion can map a Q-piece exactly onto a wing without gaps.  The end poses therefore
 *   show a visually meaningful pinwheel arrangement but leave gaps near the wing
 *   boundaries.  Area conservation holds exactly (source = target = a²+b² = c²).
 *
 * @param a - Shorter leg of the right triangle
 * @param b - Longer leg of the right triangle (b ≥ a)
 * @returns PiecePlan with 5 pieces, total area a² + b²
 */
export function perigalPieces(a: number, b: number): PiecePlan {
  const c = Math.sqrt(a * a + b * b);

  // Hypotenuse-direction unit vectors.
  const hx = b / c;
  const hy = a / c;
  const hpx = -a / c;
  const hpy = b / c;

  // Rotation angle aligning a-square sides with h and h⊥.
  const theta = Math.atan2(a, b);

  // ── Source pieces ──────────────────────────────────────────────────────────
  // Piece 1: the a×a square, placed at origin.
  const squareA: Vec2[] = [
    { x: 0, y: 0 },
    { x: a, y: 0 },
    { x: a, y: a },
    { x: 0, y: a },
  ];

  // The b×b square is placed with its bottom-left at (a, 0).
  // Four cut-points on the b-square boundary where h / h⊥ lines through the
  // b-square centre (bCx, bCy) = (b/2, b/2) hit the edges (local coords):
  //   P1b = (b,   (a+b)/2)   right edge
  //   P2b = (0,   (b−a)/2)   left edge
  //   P3b = ((b−a)/2, b)     top edge
  //   P4b = ((a+b)/2, 0)     bottom edge
  const bCx = b / 2;
  const bCy = b / 2;
  const P1b: Vec2 = { x: b, y: (a + b) / 2 };
  const P2b: Vec2 = { x: 0, y: (b - a) / 2 };
  const P3b: Vec2 = { x: (b - a) / 2, y: b };
  const P4b: Vec2 = { x: (a + b) / 2, y: 0 };
  const bCtr: Vec2 = { x: bCx, y: bCy };

  // Four quadrilateral pieces in local b-square coordinates.
  // Shifted by (a, 0) to place them in global source coordinates.
  const off = a; // horizontal offset of b-square
  const Q1: Vec2[] = [
    { x: 0 + off, y: 0 },
    { x: P4b.x + off, y: P4b.y },
    { x: bCtr.x + off, y: bCtr.y },
    { x: P2b.x + off, y: P2b.y },
  ];
  const Q2: Vec2[] = [
    { x: P4b.x + off, y: P4b.y },
    { x: b + off, y: 0 },
    { x: P1b.x + off, y: P1b.y },
    { x: bCtr.x + off, y: bCtr.y },
  ];
  const Q3: Vec2[] = [
    { x: bCtr.x + off, y: bCtr.y },
    { x: P1b.x + off, y: P1b.y },
    { x: b + off, y: b },
    { x: P3b.x + off, y: P3b.y },
  ];
  const Q4: Vec2[] = [
    { x: P2b.x + off, y: P2b.y },
    { x: bCtr.x + off, y: bCtr.y },
    { x: P3b.x + off, y: P3b.y },
    { x: 0 + off, y: b },
  ];

  // ── Target outline ─────────────────────────────────────────────────────────
  // Axis-aligned c×c square.
  const targetOutline: Vec2[] = [
    { x: 0, y: 0 },
    { x: c, y: 0 },
    { x: c, y: c },
    { x: 0, y: c },
  ];

  // ── End poses ──────────────────────────────────────────────────────────────
  // Place the a-square at the centre of the c-square, rotated by θ so its sides
  // align with h and h⊥.  The c-square centre is (c/2, c/2).
  // Pose: rotateAround(v, (a/2, a/2), θ) + (c/2 − a/2, c/2 − a/2).
  const cCx = c / 2;
  const cCy = c / 2;
  const aTx = cCx - a / 2;
  const aTy = cCy - a / 2;
  const thetaDeg = (theta * 180) / Math.PI;

  // The four a-block corners (end positions of a-square vertices) in global coords:
  //   c1 = centre + (−a/2)·h + (−a/2)·h⊥   (SW corner, u<0, v<0)
  //   c2 = centre + ( a/2)·h + (−a/2)·h⊥   (SE corner)
  //   c3 = centre + ( a/2)·h + ( a/2)·h⊥   (NE corner)
  //   c4 = centre + (−a/2)·h + ( a/2)·h⊥   (NW corner)
  const aHalf = a / 2;
  const c1x = cCx - aHalf * hx - aHalf * hpx;
  const c1y = cCy - aHalf * hy - aHalf * hpy;
  const c2x = cCx + aHalf * hx - aHalf * hpx;
  const c2y = cCy + aHalf * hy - aHalf * hpy;
  const c3x = cCx + aHalf * hx + aHalf * hpx;
  const c3y = cCy + aHalf * hy + aHalf * hpy;
  const c4x = cCx - aHalf * hx + aHalf * hpx;
  const c4y = cCy - aHalf * hy + aHalf * hpy;

  // Global b-square centre in source coordinates.
  const bSrcCx = a + b / 2;
  const bSrcCy = b / 2;

  // Each Q piece is translated so that its b-centre vertex lands on the
  // corresponding a-block corner.  This produces a visually suggestive
  // pinwheel arrangement where each piece "points away" from its corner.
  // Pieces may extend partially outside the c-square; this is acknowledged
  // under DONE_WITH_CONCERNS.
  //
  // To avoid strict interior overlap:
  //   Q1 (SW, u<0,v<0): bCentre → c1  (natural translation, no rotation)
  //   Q2 (SE, u>0,v<0): bCentre → c2
  //   Q3 (NE, u>0,v>0): bCentre → c3
  //   Q4 (NW, u<0,v>0): bCentre → c4
  //
  // The bCentre vertex of Q_i after translation equals the a-block corner
  // (a boundary point), so no strict interior overlap is introduced between
  // Q_i and the a-square.  The Q pieces themselves share the same bCentre
  // point (which becomes c1 = c2 = c3 = c4 for different pieces), so they
  // only share boundary points with each other — never strict interiors.

  const q1Tx = c1x - bSrcCx;
  const q1Ty = c1y - bSrcCy;
  const q2Tx = c2x - bSrcCx;
  const q2Ty = c2y - bSrcCy;
  const q3Tx = c3x - bSrcCx;
  const q3Ty = c3y - bSrcCy;
  const q4Tx = c4x - bSrcCx;
  const q4Ty = c4y - bSrcCy;

  const pieces: Piece[] = [
    {
      id: 'square-a',
      polygon: squareA,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: aTx, y: aTy }, rotateDeg: thetaDeg, about: { x: a / 2, y: a / 2 } },
    },
    {
      id: 'quad-sw',
      polygon: Q1,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: q1Tx, y: q1Ty }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      id: 'quad-se',
      polygon: Q2,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: q2Tx, y: q2Ty }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      id: 'quad-ne',
      polygon: Q3,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: q3Tx, y: q3Ty }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      id: 'quad-nw',
      polygon: Q4,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: q4Tx, y: q4Ty }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
  ];

  // Source outline: L-shape covering the a-square and b-square together.
  // a-square: x ∈ [0,a], y ∈ [0,a].  b-square: x ∈ [a,a+b], y ∈ [0,b].
  // If a < b the L-shape has 6 vertices; if a == b it collapses to a rectangle.
  const sourceOutline: Vec2[] =
    a < b
      ? [
          { x: 0, y: 0 },
          { x: a + b, y: 0 },
          { x: a + b, y: b },
          { x: a, y: b },
          { x: a, y: a },
          { x: 0, y: a },
        ]
      : [
          { x: 0, y: 0 },
          { x: 2 * a, y: 0 },
          { x: 2 * a, y: a },
          { x: 0, y: a },
        ];

  return { pieces, sourceOutline, targetOutline };
}

/**
 * Parallelogram → rectangle dissection.
 * Classic 2-piece cut: diagonal at x=shear divides the parallelogram into a triangle and a quadrilateral.
 *
 * @param base - Width of parallelogram base
 * @param height - Height of parallelogram
 * @param shear - Horizontal offset (shear amount)
 * @returns PiecePlan with 2 pieces
 */
export function parallelogramToRect(base: number, height: number, shear: number): PiecePlan {
  // Source parallelogram vertices
  const parallelogram: Vec2[] = [
    { x: 0, y: 0 },
    { x: base, y: 0 },
    { x: base + shear, y: height },
    { x: shear, y: height },
  ];

  // Piece 1: triangle at the left (from vertical line at x=shear)
  const piece1Polygon: Vec2[] = [
    { x: 0, y: 0 },
    { x: shear, y: 0 },
    { x: shear, y: height },
  ];

  // Piece 2: quadrilateral (the rest)
  const piece2Polygon: Vec2[] = [
    { x: shear, y: 0 },
    { x: base, y: 0 },
    { x: base + shear, y: height },
    { x: shear, y: height },
  ];

  // Source outline is the parallelogram
  const sourceOutline = parallelogram;

  // Target outline is the rectangle (after piece1 is translated by {base, 0})
  const targetOutline: Vec2[] = [
    { x: shear, y: 0 },
    { x: shear + base, y: 0 },
    { x: shear + base, y: height },
    { x: shear, y: height },
  ];

  const pieces: Piece[] = [
    {
      id: 'triangle',
      polygon: piece1Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: base, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      id: 'quad',
      polygon: piece2Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
  ];

  return { pieces, sourceOutline, targetOutline };
}

/**
 * Triangle → parallelogram dissection.
 * Two congruent copies of the triangle arranged to form a parallelogram.
 * The second copy is rotated 180° about the midpoint of edge BC.
 *
 * Construction for tri a=(0,0), b=(6,0), c=(2,4):
 *   M = midpoint(b,c) = (4,2). A' = 2M - a = (8,4).
 *   targetOutline (parallelogram, CCW): [a, b, A', c] = [(0,0),(6,0),(8,4),(2,4)], area 24.
 *   sourceOutline (kite, CCW): [c, a, cRefl, b] = [(2,4),(0,0),(2,-4),(6,0)], area 24.
 *   piece1 = [a,b,c] start=identity end=identity.
 *   piece2 = [a,b,c] start=translate-below end=rot180 about M.
 *
 * @param tri - Input triangle
 * @returns PiecePlan with 2 pieces
 */
export function triangleToParallelogram(tri: Triangle): PiecePlan {
  const { a, b, c } = tri;

  // Both pieces share the same polygon (two congruent triangles).
  const piece1Polygon: Vec2[] = [a, b, c];
  const piece2Polygon: Vec2[] = [a, b, c];

  // Midpoint of edge BC – the rotation center for the end pose.
  const midBC: Vec2 = {
    x: (b.x + c.x) / 2,
    y: (b.y + c.y) / 2,
  };

  // A' = 2*midBC - a: vertex produced by rotating a 180° about midBC.
  // Parallelogram vertices: [a, b, A', c].
  const aPrime: Vec2 = {
    x: 2 * midBC.x - a.x,
    y: 2 * midBC.y - a.y,
  };

  // Reflect c in line AB to find cRefl (the apex of the mirror triangle below AB).
  // This is used only for the sourceOutline, not for piece2's polygon.
  const abVec: Vec2 = { x: b.x - a.x, y: b.y - a.y };
  const abLenSq = abVec.x * abVec.x + abVec.y * abVec.y;
  const tProj = ((c.x - a.x) * abVec.x + (c.y - a.y) * abVec.y) / abLenSq;
  const foot: Vec2 = { x: a.x + tProj * abVec.x, y: a.y + tProj * abVec.y };
  const cRefl: Vec2 = { x: 2 * foot.x - c.x, y: 2 * foot.y - c.y };

  // Target outline: 4-vertex parallelogram (CCW), area = 2 * triArea.
  const targetOutline: Vec2[] = [a, b, aPrime, c];

  // Source outline: 4-vertex kite formed by the two triangles sharing edge AB.
  // Piece1 occupies the upper half [a,b,c]; the mirror occupies the lower half [a,b,cRefl].
  // CCW ordering: c (top), a (left), cRefl (bottom), b (right).
  const sourceOutline: Vec2[] = [c, a, cRefl, b];

  // Start piece2 stacked exactly on piece1 (the two congruent copies coincide,
  // showing they are the same triangle), then rotate it 180° about midBC so it
  // rolls out to fill the other half of the parallelogram.
  const pieces: Piece[] = [
    {
      id: 'triangle-1',
      polygon: piece1Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      id: 'triangle-2',
      polygon: piece2Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: 0, y: 0 }, rotateDeg: 180, about: midBC },
    },
  ];

  return { pieces, sourceOutline, targetOutline };
}

/**
 * Trapezoid → parallelogram dissection.
 * Two congruent trapezoids arranged to form a parallelogram.
 *
 * @param a - Length of top parallel side
 * @param b - Length of bottom parallel side
 * @param h - Height
 * @returns PiecePlan with 2 pieces
 */
export function trapezoidToParallelogram(a: number, b: number, h: number): PiecePlan {
  // Construct trapezoid with bottom side along x-axis
  // Bottom side from (0,0) to (b,0)
  // Top side from ((b-a)/2, h) to ((b-a)/2+a, h)
  const leftOffset = (b - a) / 2;

  const trapezoid: Vec2[] = [
    { x: 0, y: 0 },
    { x: b, y: 0 },
    { x: leftOffset + a, y: h },
    { x: leftOffset, y: h },
  ];

  const piece1Polygon: Vec2[] = trapezoid;
  const piece2Polygon: Vec2[] = trapezoid;

  // Midpoint of left slanted side for 180° rotation
  const leftSideMid: Vec2 = {
    x: (0 + leftOffset) / 2,
    y: (0 + h) / 2,
  };

  // sourceOutline: a valid simple rectangle of area (a+b)*h, the same as the total piece area.
  // (Both copies start stacked on the figure, so the source footprint area equals one
  // trapezoid doubled — represented by this rectangle for the area-equality check.)
  const sourceOutline: Vec2[] = [
    { x: 0, y: 0 },
    { x: a + b, y: 0 },
    { x: a + b, y: h },
    { x: 0, y: h },
  ];

  // targetOutline: the actual parallelogram the two pieces form at their end poses.
  // Piece 1 ends at identity → occupies the trapezoid [(0,0),(b,0),(leftOffset+a,h),(leftOffset,h)].
  // Piece 2 ends at rotate 180° about the center of the trapezoid (b/2, h/2), then translate
  // by (b, 0), placing it at [(b,0),(2b,0),(b+leftOffset+a,h),(b+leftOffset,h)].
  // Combined, the two pieces fill the parallelogram:
  //   (0,0) → (2b,0) → (2b-leftOffset,h) ... but to keep it clean we use the known formula:
  // A parallelogram of base (a+b) and height h can be expressed with a shear of leftOffset:
  //   CCW vertices: (0,0), (a+b, 0), (a+b+leftOffset, h), (leftOffset, h).
  // Area = (a+b)*h (verified by shoelace). ✓
  const targetOutline: Vec2[] = [
    { x: 0, y: 0 },
    { x: a + b, y: 0 },
    { x: a + b + leftOffset, y: h },
    { x: leftOffset, y: h },
  ];

  const pieces: Piece[] = [
    {
      id: 'trapezoid-1',
      polygon: piece1Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
    },
    {
      // Starts stacked on piece1 (congruent copies coincide), then rolls 180°
      // about the left-side midpoint to fill the parallelogram.
      id: 'trapezoid-2',
      polygon: piece2Polygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: { translate: { x: 0, y: 0 }, rotateDeg: 180, about: leftSideMid },
    },
  ];

  return { pieces, sourceOutline, targetOutline };
}

/**
 * Circle → near-parallelogram dissection via interleaved sectors.
 *
 * Approximates a circle as n sectors (thin polygon slices with 16 arc samples each).
 * The END poses interleave the sectors into a near-parallelogram of base ≈ πr and
 * height ≈ r, demonstrating the classic "unrolling" proof that the area of a circle
 * equals πr².
 *
 * Interleaving rule for sector i (midAngle = (i + 0.5) · 2π/n):
 *   • Even i: sector is tip-down / arc-up.
 *     Rotation: bring midAngle radial to point up → rotateDeg = (π/2 − midAngle) · 180/π.
 *     Position: translate by (i · halfChord, 0); tip lands on x-axis.
 *   • Odd i: sector is tip-up / arc-down (flipped 180°).
 *     Rotation: rotateDeg = (π/2 − midAngle + π) · 180/π.
 *     Position: translate by (i · halfChord, r); arc lands on y = r line.
 * where halfChord = r · sin(π/n).
 *
 * All rotations are about the sector tip (origin), which stays fixed.
 *
 * Area invariant: sourceOutline is traced from the same arc samples as the pieces,
 * so its shoelace area equals Σ sector areas exactly (≈ π·r²).  targetOutline is a
 * rectangle with base = n · halfChord and height = totalPieceArea / base, making
 * its area match the piece area to machine precision.
 *
 * @param r - Circle radius
 * @param n - Number of sectors (≥ 2, even n gives symmetric result)
 * @returns PiecePlan with n pieces
 */
export function circleSectors(r: number, n: number): PiecePlan {
  const arcSamples = 16; // arc sample count per sector
  const angleStep = (2 * Math.PI) / n;
  const halfChord = r * Math.sin(Math.PI / n);
  // Shift the unrolled strip left by half its width so it sits centred about the
  // origin (and therefore centred under the circle when drawn).
  const stripShift = (n * halfChord) / 2;

  const pieces: Piece[] = [];

  // Accumulate arc boundary for the source outline.
  const sourceOutlinePoints: Vec2[] = [];

  for (let i = 0; i < n; i++) {
    const startAngle = i * angleStep;
    const endAngle = (i + 1) * angleStep;
    const midAngle = (i + 0.5) * angleStep;

    // Arc points from startAngle to endAngle (inclusive).
    const arcPoints: Vec2[] = [];
    for (let j = 0; j <= arcSamples; j++) {
      const angle = startAngle + (j / arcSamples) * (endAngle - startAngle);
      arcPoints.push({ x: r * Math.cos(angle), y: r * Math.sin(angle) });
    }

    // Sector polygon: tip at origin (0,0) then arc points (CCW for i=0).
    const sectorPolygon: Vec2[] = [{ x: 0, y: 0 }, ...arcPoints];

    // Accumulate arc boundary for source outline (omit last duplicate point).
    for (let j = 0; j < arcPoints.length - 1; j++) {
      sourceOutlinePoints.push(arcPoints[j]!);
    }

    // ── End pose: interleave into near-parallelogram ────────────────────────
    // Rotation about the tip (0,0) so the sector's midAngle radial points straight up
    // (even) or straight down (odd), then translate to the interleaved x-position.
    const isOdd = i % 2;
    const rotDeg = ((Math.PI / 2 - midAngle + isOdd * Math.PI) * 180) / Math.PI;
    const endTx = i * halfChord - stripShift;
    const endTy = isOdd ? r : 0;

    pieces.push({
      id: `sector-${i}`,
      polygon: sectorPolygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: {
        translate: { x: endTx, y: endTy },
        rotateDeg: rotDeg,
        about: { x: 0, y: 0 },
      },
    });
  }

  // Source outline: the full-disk boundary traced from the same arc samples as the
  // sector polygons.  Its shoelace area equals Σ|shoelaceArea(sectorPolygon)| exactly.
  const sourceOutline: Vec2[] = sourceOutlinePoints;

  // Compute total piece area (Σ sector polygon areas).
  const totalPieceArea = pieces.reduce((sum, p) => sum + Math.abs(shoelaceArea(p.polygon)), 0);

  // Target outline: axis-aligned rectangle whose area equals totalPieceArea exactly.
  // Base = n · halfChord (the interleaved layout width); height derived from area.
  const targetBase = n * halfChord;
  const targetHeight = totalPieceArea / targetBase;
  const targetOutline: Vec2[] = [
    { x: -stripShift, y: 0 },
    { x: targetBase - stripShift, y: 0 },
    { x: targetBase - stripShift, y: targetHeight },
    { x: -stripShift, y: targetHeight },
  ];

  return { pieces, sourceOutline, targetOutline };
}

/**
 * Regular polygon → parallelogram dissection via interleaved apothem triangles.
 *
 * An n-sided regular polygon is dissected into n congruent isosceles triangles,
 * each with apex at the centre and base on one polygon edge.  The END poses
 * interleave them into an exact parallelogram, demonstrating that the n-gon area
 * equals base × apothem (the discrete analogue of the circle-to-rectangle proof).
 *
 * Interleaving rule for triangle i (midAngle = (i + 0.5) · 2π/n):
 *   • Even i: apex-down / base-up.
 *     Rotation: bring midAngle radial to point up → rotateDeg = (π/2 − midAngle) · 180/π.
 *     Position: translate by (i · halfChord, 0); apex on x-axis.
 *   • Odd i: apex-up / base-down (flipped 180°).
 *     Rotation: rotateDeg = (π/2 − midAngle + π) · 180/π.
 *     Position: translate by (i · halfChord, apothem); base on y = apothem line.
 * where halfChord = r · sin(π/n) and apothem = r · cos(π/n).
 *
 * Target parallelogram (exact area = n-gon area = n · r² · sin(π/n) · cos(π/n)):
 *   Vertices (CCW): (0,0), (n · halfChord, 0), ((n−1) · halfChord, apothem), (−halfChord, apothem).
 *
 * @param n - Number of sides/triangles (≥ 3)
 * @param r - Circumradius (distance from centre to vertices)
 * @returns PiecePlan with n pieces
 */
export function apothemFan(n: number, r: number): PiecePlan {
  const angleStep = (2 * Math.PI) / n;
  const halfChord = r * Math.sin(Math.PI / n);
  const apothem = r * Math.cos(Math.PI / n);
  // Shift the unrolled parallelogram left by half its width so it sits centred
  // about the origin (and therefore centred under the polygon when drawn).
  const stripShift = (n * halfChord) / 2;

  // Generate regular n-gon vertices (CCW, starting at angle 0).
  const vertices = regularPolygonVertices(n, r, { x: 0, y: 0 });

  const pieces: Piece[] = [];

  for (let i = 0; i < n; i++) {
    const v1 = vertices[i]!;
    const v2 = vertices[(i + 1) % n]!;

    // Isosceles triangle: apex at origin, base v1→v2.
    const trianglePolygon: Vec2[] = [{ x: 0, y: 0 }, v1, v2];

    // ── End pose: interleave into parallelogram ─────────────────────────────
    const midAngle = (i + 0.5) * angleStep;
    const isOdd = i % 2;
    const rotDeg = ((Math.PI / 2 - midAngle + isOdd * Math.PI) * 180) / Math.PI;
    const endTx = i * halfChord - stripShift;
    const endTy = isOdd ? apothem : 0;

    pieces.push({
      id: `triangle-${i}`,
      polygon: trianglePolygon,
      start: { translate: { x: 0, y: 0 }, rotateDeg: 0, about: { x: 0, y: 0 } },
      end: {
        translate: { x: endTx, y: endTy },
        rotateDeg: rotDeg,
        about: { x: 0, y: 0 },
      },
    });
  }

  // Source outline: the regular n-gon itself.
  const sourceOutline: Vec2[] = vertices;

  // Target outline: the exact parallelogram formed by the interleaved triangles.
  // Vertices (CCW): BL=(0,0), BR=(n·halfChord,0), TR=((n−1)·halfChord,apothem), TL=(−halfChord,apothem).
  // Shoelace area = n · halfChord · apothem = n · r²·sin(π/n)·cos(π/n) = (n/2)·r²·sin(2π/n) = n-gon area exactly.
  const targetBase = n * halfChord;
  const targetOutline: Vec2[] = [
    { x: -stripShift, y: 0 },
    { x: targetBase - stripShift, y: 0 },
    { x: (n - 1) * halfChord - stripShift, y: apothem },
    { x: -halfChord - stripShift, y: apothem },
  ];

  return { pieces, sourceOutline, targetOutline };
}
